import { useRef, useCallback, useEffect } from "react";

export interface MbiraSettings {
  volume: number;
  decay: number;
  buzz: number; // Bottle cap rattle - more prominent on mbira
  reverb: number;
  tone: number;
}

export function useMbiraSynth() {
  const ctxRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);
  const noiseBufferRef = useRef<AudioBuffer | null>(null);
  const reverbNodeRef = useRef<ConvolverNode | null>(null);
  const reverbGainRef = useRef<GainNode | null>(null);
  const dryGainRef = useRef<GainNode | null>(null);

  const settingsRef = useRef<MbiraSettings>({
    volume: 0.8,
    decay: 0.6,
    buzz: 0.4, // Higher default - mbira traditionally has bottle caps
    reverb: 0.25,
    tone: 0.5, // Darker tone by default
  });

  const init = useCallback(() => {
    if (ctxRef.current) {
      if (ctxRef.current.state === "suspended") {
        ctxRef.current.resume();
      }
      return ctxRef.current;
    }

    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    ctxRef.current = ctx;

    if (ctx.state === "suspended") {
      ctx.resume();
    }

    // Noise buffer for attack transients
    if (!noiseBufferRef.current) {
      const bufferSize = ctx.sampleRate * 0.05;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      noiseBufferRef.current = buffer;
    }

    // Reverb - simulates the deze (gourd resonator)
    if (!reverbNodeRef.current) {
      const reverbTime = 2.0; // Longer reverb for gourd resonance
      const sampleRate = ctx.sampleRate;
      const length = sampleRate * reverbTime;
      const impulse = ctx.createBuffer(2, length, sampleRate);

      for (let channel = 0; channel < 2; channel++) {
        const channelData = impulse.getChannelData(channel);
        for (let i = 0; i < length; i++) {
          // Gourd-like resonance with early reflections
          const earlyReflection = i < sampleRate * 0.05 ? 1.5 : 1;
          channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.0) * earlyReflection;
        }
      }

      reverbNodeRef.current = ctx.createConvolver();
      reverbNodeRef.current.buffer = impulse;
    }

    // Master chain
    masterGainRef.current = ctx.createGain();
    masterGainRef.current.gain.value = settingsRef.current.volume;

    dryGainRef.current = ctx.createGain();
    dryGainRef.current.gain.value = 1 - settingsRef.current.reverb * 0.5;

    reverbGainRef.current = ctx.createGain();
    reverbGainRef.current.gain.value = settingsRef.current.reverb;

    compressorRef.current = ctx.createDynamicsCompressor();
    compressorRef.current.threshold.value = -20;
    compressorRef.current.knee.value = 8;
    compressorRef.current.ratio.value = 12;
    compressorRef.current.attack.value = 0.002;
    compressorRef.current.release.value = 0.15;

    masterGainRef.current.connect(dryGainRef.current);
    dryGainRef.current.connect(compressorRef.current);

    masterGainRef.current.connect(reverbNodeRef.current);
    reverbNodeRef.current.connect(reverbGainRef.current);
    reverbGainRef.current.connect(compressorRef.current);

    compressorRef.current.connect(ctx.destination);

    return ctx;
  }, []);

  const updateSettings = useCallback((settings: Partial<MbiraSettings>) => {
    settingsRef.current = { ...settingsRef.current, ...settings };
    if (masterGainRef.current && settings.volume !== undefined) {
      masterGainRef.current.gain.value = settings.volume;
    }
    if (reverbGainRef.current && settings.reverb !== undefined) {
      reverbGainRef.current.gain.value = settings.reverb;
      if (dryGainRef.current) {
        dryGainRef.current.gain.value = 1 - settings.reverb * 0.5;
      }
    }
  }, []);

  const playKey = useCallback(
    (freq: number, panValue = 0, velocity = 127) => {
      const ctx = init();
      if (ctx.state === "suspended") ctx.resume();

      const now = ctx.currentTime;
      const { decay, buzz, tone } = settingsRef.current;

      // Mbira has slightly shorter decay than kalimba
      const pitchScale = Math.max(0.15, 1 - (freq - 196) / 4000);
      const fundamentalDecay = (0.8 + decay * 5.0) * pitchScale;

      const velocityScale = velocity / 127;
      const master = ctx.createGain();
      master.gain.value = 0.28 * velocityScale;

      const panner = ctx.createStereoPanner();
      panner.pan.value = panValue;

      // Tone filter - mbira is darker/woodier
      const toneFilter = ctx.createBiquadFilter();
      toneFilter.type = "lowpass";
      toneFilter.frequency.value = 600 + tone * 3500; // 600Hz - 4100Hz (darker range)
      toneFilter.Q.value = 0.7;

      master.connect(toneFilter);
      toneFilter.connect(panner);
      panner.connect(masterGainRef.current!);

      // --- 1. TONAL COMPONENT ---

      // Fundamental - sine with slight warmth
      const fundOsc = ctx.createOscillator();
      fundOsc.type = "sine";
      fundOsc.frequency.value = freq;

      const fundGain = ctx.createGain();
      fundGain.gain.setValueAtTime(0, now);
      fundGain.gain.linearRampToValueAtTime(1.0, now + 0.003);
      fundGain.gain.exponentialRampToValueAtTime(0.0001, now + fundamentalDecay);

      fundOsc.connect(fundGain);
      fundGain.connect(master);

      // 2nd harmonic - adds body
      const harm2Osc = ctx.createOscillator();
      harm2Osc.type = "sine";
      harm2Osc.frequency.value = freq * 2;

      const harm2Gain = ctx.createGain();
      harm2Gain.gain.setValueAtTime(0, now);
      harm2Gain.gain.linearRampToValueAtTime(0.25, now + 0.002);
      harm2Gain.gain.exponentialRampToValueAtTime(0.0001, now + fundamentalDecay * 0.7);

      harm2Osc.connect(harm2Gain);
      harm2Gain.connect(master);

      // 3rd harmonic - slight edge
      const harm3Osc = ctx.createOscillator();
      harm3Osc.type = "sine";
      harm3Osc.frequency.value = freq * 3;

      const harm3Gain = ctx.createGain();
      harm3Gain.gain.setValueAtTime(0, now);
      harm3Gain.gain.linearRampToValueAtTime(0.08, now + 0.002);
      harm3Gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);

      harm3Osc.connect(harm3Gain);
      harm3Gain.connect(master);

      // --- 2. ATTACK TRANSIENTS ---

      // Thumb pluck noise
      const clickSrc = ctx.createBufferSource();
      if (noiseBufferRef.current) {
        clickSrc.buffer = noiseBufferRef.current;
      }

      const clickFilter = ctx.createBiquadFilter();
      clickFilter.type = "bandpass";
      clickFilter.frequency.value = 2000;
      clickFilter.Q.value = 1.2;

      const clickGain = ctx.createGain();
      clickGain.gain.setValueAtTime(0.25, now);
      clickGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);

      clickSrc.connect(clickFilter);
      clickFilter.connect(clickGain);
      clickGain.connect(master);

      // Wood body resonance
      const bodyFreq = Math.max(80, freq * 0.25);
      const bodyOsc = ctx.createOscillator();
      bodyOsc.type = "sine";
      bodyOsc.frequency.setValueAtTime(bodyFreq, now);
      bodyOsc.frequency.exponentialRampToValueAtTime(bodyFreq * 0.6, now + 0.1);

      const bodyGain = ctx.createGain();
      bodyGain.gain.setValueAtTime(0.35, now);
      bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);

      const bodyFilter = ctx.createBiquadFilter();
      bodyFilter.type = "lowpass";
      bodyFilter.frequency.value = 200;

      bodyOsc.connect(bodyFilter);
      bodyFilter.connect(bodyGain);
      bodyGain.connect(master);

      // --- 3. BUZZ (bottle caps / machachara) ---
      // This is MORE prominent on mbira than kalimba
      const buzzDuration = 0.2 + decay * 0.3;

      if (buzz > 0.01) {
        // Multiple detuned oscillators for rich metallic buzz
        const buzzFreqs = [
          freq * 2.01,  // Slightly sharp 2nd
          freq * 2.99,  // Slightly flat 3rd
          freq * 4.02,  // Sharp 4th
          freq * 5.98,  // Flat 6th
        ];

        const buzzMaster = ctx.createGain();
        buzzMaster.gain.setValueAtTime(0, now);
        buzzMaster.gain.linearRampToValueAtTime(buzz * 0.18, now + 0.004);
        buzzMaster.gain.exponentialRampToValueAtTime(0.0001, now + buzzDuration);
        buzzMaster.connect(master);

        buzzFreqs.forEach((f, i) => {
          const buzzOsc = ctx.createOscillator();
          buzzOsc.type = i % 2 === 0 ? "sawtooth" : "square";
          buzzOsc.frequency.value = f;

          const individualGain = ctx.createGain();
          individualGain.gain.value = 0.25; // Distribute across oscillators

          buzzOsc.connect(individualGain);
          individualGain.connect(buzzMaster);

          buzzOsc.start(now);
          buzzOsc.stop(now + buzzDuration + 0.05);
        });

        // Add some noise-based rattle
        const rattleSrc = ctx.createBufferSource();
        if (noiseBufferRef.current) {
          rattleSrc.buffer = noiseBufferRef.current;
          rattleSrc.loop = true;
        }

        const rattleFilter = ctx.createBiquadFilter();
        rattleFilter.type = "bandpass";
        rattleFilter.frequency.value = freq * 4;
        rattleFilter.Q.value = 3;

        const rattleGain = ctx.createGain();
        rattleGain.gain.setValueAtTime(buzz * 0.08, now);
        rattleGain.gain.exponentialRampToValueAtTime(0.0001, now + buzzDuration * 0.5);

        rattleSrc.connect(rattleFilter);
        rattleFilter.connect(rattleGain);
        rattleGain.connect(master);

        rattleSrc.start(now);
        rattleSrc.stop(now + buzzDuration);
      }

      // Start oscillators
      fundOsc.start(now);
      harm2Osc.start(now);
      harm3Osc.start(now);
      clickSrc.start(now);
      bodyOsc.start(now);

      // Stop & cleanup
      const maxDuration = fundamentalDecay + 0.5;

      fundOsc.stop(now + maxDuration);
      harm2Osc.stop(now + maxDuration);
      harm3Osc.stop(now + maxDuration);
      bodyOsc.stop(now + 0.2);

      setTimeout(() => {
        master.disconnect();
        fundGain.disconnect();
        harm2Gain.disconnect();
        harm3Gain.disconnect();
        clickGain.disconnect();
        bodyGain.disconnect();
        toneFilter.disconnect();
      }, maxDuration * 1000);
    },
    [init]
  );

  // Handle visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && ctxRef.current) {
        // Try to resume immediately
        ctxRef.current.resume();
        // Retry after a short delay (mobile sometimes needs this)
        setTimeout(() => {
          if (ctxRef.current?.state === "suspended") {
            ctxRef.current.resume();
          }
        }, 100);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (ctxRef.current) {
        ctxRef.current.close();
        ctxRef.current = null;
      }
    };
  }, []);

  return { playKey, updateSettings, init };
}

export function getDefaultSettings(): MbiraSettings {
  return { volume: 0.8, decay: 0.6, buzz: 0.4, reverb: 0.25, tone: 0.5 };
}
